import { useCallback, useEffect, useReducer, useRef, useState } from "react";

/** Inputs worth landing the cursor in when a question scrolls into view. */
const FOCUS_TARGET =
  'input:not([type="hidden"]):not([disabled]), textarea:not([disabled])';

/** Roughly how long a smooth `scrollIntoView` takes. */
const PROGRAMMATIC_SCROLL_MS = 700;

/**
 * The questions on screen: every answered one in order, plus the first
 * unanswered one. A question added or removed mid-flow (e.g. the original
 * filing for an RFE) slots in without touching the answers around it.
 */
export const visibleTypeformSteps = <Step extends string>(
  steps: readonly Step[],
  confirmed: ReadonlySet<Step>
): Step[] => {
  const visible: Step[] = [];
  for (const step of steps) {
    visible.push(step);
    if (!confirmed.has(step)) break;
  }
  return visible;
};

type Options<Step extends string> = {
  steps: readonly Step[];
  isComplete: (step: Step) => boolean;
};

/**
 * A vertical typeform: answered questions stay on the page, OK reveals the
 * next one and scrolls it to the middle, and scrolling (or clicking) back to
 * an earlier question makes it the active one again.
 */
export const useTypeformFlow = <Step extends string>({
  steps,
  isComplete
}: Options<Step>) => {
  const [confirmed, setConfirmed] = useState<ReadonlySet<Step>>(
    () => new Set()
  );
  const [active, setActive] = useState<Step>(steps[0]);
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const sections = useRef(new Map<Step, HTMLElement>());
  const sectionRefs = useRef(new Map<Step, (el: HTMLElement | null) => void>());
  // The question to focus (and scroll to) once it is on screen. Starts on the
  // first question, unscrolled, so its keyboard shortcuts work straight away.
  const pendingFocus = useRef<{ step: Step; scroll: boolean } | null>(
    steps[0] ? { step: steps[0], scroll: false } : null
  );

  // Until this time, scroll-driven activation is ignored so a smooth scroll to
  // a question doesn't activate every question it passes on the way.
  const programmaticScrollUntil = useRef(0);

  // Runs after every render: the target may only mount a render or two after
  // it was requested (e.g. once a preloaded client finishes loading).
  useEffect(() => {
    const pending = pendingFocus.current;
    if (!pending) return;
    const el = sections.current.get(pending.step);
    if (!el) return;
    pendingFocus.current = null;
    if (pending.scroll) {
      programmaticScrollUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
      el.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }
    const target = el.querySelector<HTMLElement>(FOCUS_TARGET) ?? el;
    target.focus({ preventScroll: true });
  });

  // Scrolling a question into the middle of the screen makes it active. If the
  // cursor was left in another question, it moves to this one's section so
  // typing and Enter don't land in a question the user has scrolled away from.
  const observer = useRef<IntersectionObserver | null>(null);
  const getObserver = useCallback(() => {
    if (observer.current || typeof IntersectionObserver === "undefined") {
      return observer.current;
    }
    observer.current = new IntersectionObserver(
      (entries) => {
        if (Date.now() < programmaticScrollUntil.current) return;
        const entry = entries.find((e) => e.isIntersecting);
        if (!entry) return;
        const el = entry.target as HTMLElement;
        const step = [...sections.current].find(([, s]) => s === el)?.[0];
        if (!step) return;
        setActive(step);
        const focused = document.activeElement;
        const focusIsInAnotherQuestion =
          focused instanceof HTMLElement &&
          !el.contains(focused) &&
          [...sections.current.values()].some((s) => s.contains(focused));
        if (focusIsInAnotherQuestion) el.focus({ preventScroll: true });
      },
      { rootMargin: "-45% 0px -45% 0px" }
    );
    return observer.current;
  }, []);
  useEffect(
    () => () => {
      observer.current?.disconnect();
      observer.current = null;
    },
    []
  );

  /** Make `step` active, then scroll to it and focus it. */
  const focusStep = useCallback((step: Step) => {
    pendingFocus.current = { step, scroll: true };
    programmaticScrollUntil.current = Date.now() + PROGRAMMATIC_SCROLL_MS;
    setActive(step);
    // Re-focusing the question that is already active changes no state.
    rerender();
  }, []);

  const activate = useCallback((step: Step) => setActive(step), []);

  /** Mark `step` answered and move on to the question after it. */
  const confirm = useCallback(
    (step: Step) => {
      setConfirmed((prev) => new Set(prev).add(step));
      const order = stepsRef.current;
      const next = order[order.indexOf(step) + 1];
      if (next) focusStep(next);
    },
    [focusStep]
  );

  /** Un-answer `step`; the questions after it hide until it is answered again. */
  const unconfirm = useCallback((step: Step) => {
    setConfirmed((prev) => {
      if (!prev.has(step)) return prev;
      const next = new Set(prev);
      next.delete(step);
      return next;
    });
  }, []);

  const sectionRef = useCallback(
    (step: Step) => {
      let ref = sectionRefs.current.get(step);
      if (!ref) {
        ref = (el) => {
          const previous = sections.current.get(step);
          if (previous) getObserver()?.unobserve(previous);
          if (el) {
            sections.current.set(step, el);
            getObserver()?.observe(el);
          } else {
            sections.current.delete(step);
          }
        };
        sectionRefs.current.set(step, ref);
      }
      return ref;
    },
    [getObserver]
  );

  const visibleSteps = visibleTypeformSteps(steps, confirmed);
  const activeStep = visibleSteps.includes(active)
    ? active
    : visibleSteps[visibleSteps.length - 1];
  const answeredCount = visibleSteps.filter((step) =>
    confirmed.has(step)
  ).length;

  return {
    visibleSteps,
    activeStep,
    isLastStep: (step: Step) => step === steps[steps.length - 1],
    canSubmit: steps.every(isComplete),
    progress: steps.length > 0 ? (answeredCount / steps.length) * 100 : 0,
    activate,
    focusStep,
    confirm,
    unconfirm,
    sectionRef
  };
};
